import {useState, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import {useLocation, Link} from 'react-router-dom'

import {signUp, showAlert} from '../../actions'

import {useIsMounted} from '../../hooks'

import {checkIsEmailAvailableUsingToken} from '../../apis'

import {Errors, SlideTransition, ThreeDotProgress} from '../generics'

import SignUpForm from './SignUpForm'
import AppleAuthButton from './AppleAuthButton'
import GoogleAuthButton from './GoogleAuthButton'
import AccountRegistrationForm from './AccountRegistrationForm'
import EmailConfirmationNotice from './EmailConfirmationNotice'
import RegistrationSkeleton from './RegistrationSkeleton'

import PrivacyPolicy from '../legal/PrivacyPolicy'
import TermsAndConditions from '../legal/TermsAndConditions'

import Dialog from '@mui/material/Dialog'



const styles = {
  root: css`
    width: 100%;
    min-height: 550px;
    background-color: #fff;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
  `,
  signupContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding-top: 30px;
  `,
  emailButton: css`
    width: 100%;
    height: 44px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-transform: none;
    border: none;
    border-radius: 4px;
    background-color: #DEDEDE;
    cursor: pointer;
    margin-bottom: 15px;
    border-radius: 22px;
    text-align: center;
    font-size: 0.9rem;
    color: #5a5a5a;
    font-weight: 500;
  `,
  title: css`
    font-size: 2.2rem;
    letter-spacing: -1px;
    font-weight: 700;
    margin: 0;
    width: 100%;
    text-align: center;
    @media (max-width: 600px) {
      font-size: 1.9rem;
    }
  `,
  subtitle: css`
    text-transform: uppercase;
    color: grey;
    width: 100%;
    text-align: center;
    margin-bottom: 30px;
    @media (max-width: 375px) {
      margin-bottom: 25px;
    }
  `,
  separator: css`
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: center;
    margin: 30px 0;
    color: #979797;
    font-weight: 600;
    font-size: 0.9rem;
    hr {
      width: 100%;
    }
    @media (max-width: 600px) {
      margin: 15px 0;
    }
  `,
  buttonContainer: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  button: css`
    min-height: 44px;
    max-height: 44px;
    overflow: hidden;
  `,
  caption: css`
    text-align: center;
    font-size: 0.9rem;
    font-weight: 500;
    color: #979797;
  `,
  link1: css`
    color: #ff0055;
    font-weight: 500;
    cursor: pointer;
  `,
  link2: css`
    text-decoration: none;
    color: #ff0055;
    font-weight: 500;
    cursor: pointer;
    font-size: 1rem;
  `,
  termsAndConditions: css`
    margin: 20px 0;
    text-align: center;
    font-size: 0.9rem;
    font-weight: 500;
    color: #979797;
  `
}



const SignUp = ({signUp, showAlert, close, callback}) => {

  const isMounted = useIsMounted()
  const location  = useLocation()

  const [step, setStep]   = useState(1)
  const [error, setError] = useState(false)
  const [modal, setModal] = useState(false)
  const [isFetching, setIsFetching] = useState(false)


  const [params, setParams] = useState({
    type: location.state?.type || 'user',
    email: '',
    token: '',
    method: '',
    utm_source: sessionStorage.getItem('UTMSource'),
    utm_medium: sessionStorage.getItem('UTMMedium'),
    utm_campaign: sessionStorage.getItem('UTMCampaign'),
  })


  const handlePostSignup = useCallback((response) => {
    if (!isMounted.current) return
    setIsFetching(false)

    if (params.method === 'email' && step === 2) {
      setStep(3)
    } else if (params.type !== 'user' && step === 3) {
      setStep(4) // show AccountRegistrationForm
    } else {
      showAlert('Welcome to Ckord!', 'success')
      close?.()
      callback?.()
    }
  }, [
    step,
    close,
    callback,
    showAlert,
    isMounted,
    params.type, 
    params.method, 
    setIsFetching 
  ])



// Google sign up
const googleSignUpCallback = useCallback(async (token) => {
  setIsFetching(true)
  setError(null)

  try {
    // first check email availability
    const { data: isAvailable } = await checkIsEmailAvailableUsingToken(token)
    if (!isAvailable) {
      setError('Email already taken')
      setIsFetching(false)
      return
    }

    // then perform the signup
    const userPayload = { ...params, method: 'google', token }
    await signUp(userPayload)
    handlePostSignup()
  } catch (e) {
    if (isMounted.current) {
      setError('Something went wrong')
      setIsFetching(false)
    }
    showAlert('Something went wrong', 'error')
  }
}, [params, signUp, handlePostSignup, showAlert, isMounted])


// Apple sign up
const appleSignUpCallback = useCallback(async (token) => {
  setIsFetching(true)
  setError(null)

  try {
    const userPayload = { ...params, method: 'apple', token }
    await signUp(userPayload)
    handlePostSignup()
  } finally {
    if (isMounted.current) setIsFetching(false)
  }
}, [params, signUp, handlePostSignup, isMounted])



  // Email sign up
  const startEmailSignup = useCallback(() => {
    setError(null)
    setParams(prev => ({ ...prev, method: 'email' }))
    setStep(2)
  }, [])



  const goBack = useCallback(() => {
    setError(null)
    setParams(p => ({ ...p, method: '', email: '', token: '' }))
    setStep(1)
  }, [])



  const selectStep = (step) => () => {
    setStep(step)
  }

  const selectModal = (type) => () => {
    setModal(type)
  }

  const closeModal = () => {
    setModal(null)
  }



  const stepComponents = {
    1: (
      <div css={styles.signupContainer}>
        <Errors errors={error} margin='0px 0px 15px 0px' />
        <div css={styles.buttonContainer}>
          <AppleAuthButton
            mode='sign-up'
            signUpWithApple={appleSignUpCallback}
            setIsFetching={setIsFetching}
            setError={setError}
          />
          <GoogleAuthButton
            shape='pill'
            context='signup'
            text='signup_with'
            callback={googleSignUpCallback}
          />
        </div>
        <div css={styles.separator}>
          <hr />OR<hr />
        </div>

        <button css={styles.emailButton} onClick={startEmailSignup}>
          Continue with Email
        </button>

        <p css={styles.termsAndConditions}>
          By signing up, you agree with our<br/>
          <span onClick={selectModal('terms')} css={styles.link1}>
            Terms & Conditions
          </span>&nbsp;and&nbsp;
          <span onClick={selectModal('privacy')} css={styles.link1}>
            Privacy Policy
          </span>.
        </p>
      </div>
    ),
    2: (
      <SignUpForm
        {...params}
        setIsLoading={setIsFetching}
        onSuccess={handlePostSignup}
        onError={setError}
      />
    ),
    3: (
      <EmailConfirmationNotice
        type={params.type}
        callback={handlePostSignup}
        nextStep={selectStep(4)}
      />
    ),
    4: <AccountRegistrationForm type={params.type} />
  }


  function getTitle() {
    switch (step) {
      case 1:
      case 2:
        return 'Sign Up'
      case 3:
        return 'Confirm Email'
      case 4:
        return 'Select Name'
      default:
        return ''
    }
  }


  return (
    <>
      {isFetching &&
        <ThreeDotProgress position='absolute' radius={4} background='rgba(255,255,255,0.8)' />
      }

      <RegistrationSkeleton
        title={getTitle()}
        subtitle={step===1 ? 'SIGN UP USING' : `${params.method} SIGN UP`}
        caption={
          step < 3 ?
            <p css={styles.caption}>
              Already have an account?<br/>
              <Link css={styles.link2} to='/sign-in'>
                Sign In
              </Link>
            </p> : null
        }
        goBack={goBack}
        isBackButtonActive={step === 2}
      >
        {stepComponents[step] ?? null}
      </RegistrationSkeleton>


      <Dialog
        open={Boolean(modal)}
        onClose={closeModal}
        fullScreen
        TransitionComponent={SlideTransition}
      >
        {modal === 'privacy' && <PrivacyPolicy close={closeModal} isShowingHeader />}
        {modal === 'terms'   && <TermsAndConditions close={closeModal} isShowingHeader />}
      </Dialog>
    </>
  )
}


export default connect(null, {signUp, showAlert})(SignUp)


